import { useCallback } from "react";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";

/** What a usage read answered for one item: the edit verdict and the count. */
export interface CatalogItemUsageAnswer {
  mayEdit: boolean;
  usageCount: number;
}

/**
 * One item's usage, as its usage readers last answered it — the donor's
 * `load-item-spec-usage` cache entry, which the card kebab, the item dialog's
 * header menu and its Spec Usage share (`itemsApi.ts:95-126`), and which
 * `load-item` never writes: a variant preview ships no usage on purpose
 * (falcon `shared/router.py:92-99`). Atlas's usage arrives on the item's
 * details, which a preview reads too, so this key is apart from the detail's
 * and only a usage reader writes it, with `record` — a query-client entry, not
 * a store beside it (ADR-0014 §2). Never fetched itself.
 */
export function useCatalogItemUsage(itemId: number) {
  const { data } = useQuery({ queryKey: catalogItemUsageQueryKey(itemId), queryFn: skipToken });
  return { answer: data as CatalogItemUsageAnswer | undefined, record: useRecordItemUsage() };
}

/** A usage reader's write: what it read for an item, for every reader after it. */
export function useRecordItemUsage() {
  const queryClient = useQueryClient();
  return useCallback(
    (id: number, answer: CatalogItemUsageAnswer) =>
      queryClient.setQueryData(catalogItemUsageQueryKey(id), answer),
    [queryClient],
  );
}

export const catalogItemUsageQueryKey = (itemId: number) => ["catalog-item-usage", itemId] as const;
